import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bot, Check, KeyRound, ListChecks, LogOut, Moon, Plus, Search, Shield, Sun, User } from 'lucide-react';

import type { Project } from '@tasks/contracts';

import { quickSearch } from '../api/search';
import { listProjects } from '../api/projects';
import { queryKeys } from '../api/queryKeys';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { Dropdown } from '../components/Dropdown';
import { IconButton } from '../components/IconButton';
import { Logo } from '../components/Logo';
import { useAuth } from '../auth/AuthProvider';
import { NotificationPanel } from '../features/notifications/NotificationPanel';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useHotkeys } from '../hooks/useHotkeys';
import { useUiStore } from '../stores/uiStore';
import { cn } from '../lib/cn';
import styles from './AppHeader.module.css';

/** Header: логотип, переключатель проекта, поиск, «Создать», меню аватара. */
export function AppHeader({ project }: { readonly project: Project | null }) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const theme = useUiStore((state) => state.theme);
  const toggleTheme = useUiStore((state) => state.toggleTheme);
  const setQuickCreateOpen = useUiStore((state) => state.setQuickCreateOpen);
  const setHotkeyHelpOpen = useUiStore((state) => state.setHotkeyHelpOpen);

  const [query, setQuery] = useState('');
  const [isSuggestOpen, setSuggestOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const debounced = useDebouncedValue(query, 300);

  const projectsQuery = useQuery({
    queryKey: queryKeys.projects.list('false'),
    queryFn: ({ signal }) => listProjects('false', signal),
    staleTime: 60_000,
  });

  const suggestQuery = useQuery({
    queryKey: queryKeys.quickSearch(debounced),
    queryFn: ({ signal }) => quickSearch(debounced, 20, signal),
    enabled: debounced.trim().length >= 2,
  });

  useHotkeys({
    '/': () => searchRef.current?.focus(),
    c: () => setQuickCreateOpen(true),
    '?': () => setHotkeyHelpOpen(true),
  });

  useEffect(() => {
    if (query.trim().length < 2) {
      setSuggestOpen(false);
    }
  }, [query]);

  const projects = projectsQuery.data ?? [];
  const suggestions = suggestQuery.data?.items ?? [];

  const submitSearch = (): void => {
    if (query.trim().length === 0) {
      return;
    }
    setSuggestOpen(false);
    navigate(`/search?jql=${encodeURIComponent(query.trim())}`);
  };

  return (
    <header className={styles['header']}>
      <Link to="/" className={styles['logo']} aria-label="Tasks! — на главную">
        <Logo size="sm" />
      </Link>

      <Dropdown
        align="start"
        trigger={({ toggle, isOpen }) => (
          <button
            type="button"
            data-testid="project-switcher"
            className={styles['projectSwitch']}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            onClick={toggle}
          >
            {project ? (
              <>
                <span className={styles['projectKey']}>{project.key}</span>
                <span className={styles['projectName']}>{project.name}</span>
              </>
            ) : (
              <span className={styles['projectName']}>Выбрать проект</span>
            )}
            <span aria-hidden className={styles['caret']}>
              ▾
            </span>
          </button>
        )}
        header={<span>Проекты</span>}
        items={projects.map((item) => ({
          id: item.id,
          label: `${item.key} · ${item.name}`,
          icon: item.key === project?.key ? <Check size={14} /> : undefined,
          onSelect: () => navigate(`/projects/${item.key}/board`),
        }))}
      />

      <div className={styles['searchWrap']}>
        <div className={styles['search']}>
          <Search aria-hidden size={16} className={styles['searchIcon']} />
          <input
            ref={searchRef}
            value={query}
            className={styles['searchInput']}
            placeholder="Поиск: ключ, заголовок или JQL"
            aria-label="Глобальный поиск"
            onChange={(event) => {
              setQuery(event.target.value);
              setSuggestOpen(true);
            }}
            onFocus={() => setSuggestOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                submitSearch();
              }
              if (event.key === 'Escape') {
                setSuggestOpen(false);
              }
            }}
          />
          <kbd className={styles['kbd']}>/</kbd>
        </div>

        {isSuggestOpen && debounced.trim().length >= 2 ? (
          <div className={styles['suggest']} role="listbox" aria-label="Результаты поиска">
            {suggestQuery.isPending ? (
              <p className={styles['suggestState']}>Поиск…</p>
            ) : suggestions.length === 0 ? (
              <p className={styles['suggestState']}>Ничего не найдено</p>
            ) : (
              suggestions.map((issue) => (
                <button
                  key={issue.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className={styles['suggestItem']}
                  onClick={() => {
                    setSuggestOpen(false);
                    navigate(`/projects/${issue.key.split('-')[0]}/board?issue=${issue.key}`);
                  }}
                >
                  <span className={styles['suggestKey']}>{issue.key}</span>
                  <span className="u-truncate">{issue.summary}</span>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>

      <Button
        variant="primary"
        size="sm"
        icon={<Plus size={16} />}
        onClick={() => setQuickCreateOpen(true)}
        disabled={!project && projects.length === 0}
      >
        Создать
      </Button>

      <IconButton
        label={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
        tooltip={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
        icon={theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        onClick={toggleTheme}
      />

      <IconButton
        label="Горячие клавиши"
        tooltip="Горячие клавиши (?)"
        icon={<KeyRound size={18} />}
        onClick={() => setHotkeyHelpOpen(true)}
      />

      <NotificationPanel />

      <Dropdown
        align="end"
        trigger={({ toggle, isOpen }) => (
          <button
            type="button"
            className={cn(styles['avatarButton'], isOpen && styles['avatarButtonOpen'])}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-label="Меню пользователя"
            onClick={toggle}
          >
            <Avatar user={user} size="sm" showTooltip={false} />
          </button>
        )}
        header={<span>{user?.displayName ?? 'Гость'}</span>}
        items={[
          // Вход в админку видит только глобальный администратор (docs/06 §2):
          // без него страницы /admin/* достижимы лишь по прямому URL.
          ...(user?.role === 'admin'
            ? [
                {
                  id: 'agents',
                  label: 'AI-агенты',
                  icon: <Bot size={14} />,
                  onSelect: () => navigate('/agents'),
                },
              ]
            : []),
          ...(user?.role === 'admin'
            ? [
                {
                  id: 'admin',
                  label: 'Администрирование',
                  icon: <Shield size={14} />,
                  onSelect: () => navigate('/admin/users'),
                },
              ]
            : []),
          {
            id: 'my-board',
            label: 'Моя доска',
            icon: <ListChecks size={14} />,
            onSelect: () => navigate('/my'),
          },
          {
            id: 'profile',
            label: 'Профиль',
            icon: <User size={14} />,
            onSelect: () => navigate('/profile'),
          },
          {
            id: 'password',
            label: 'Сменить пароль',
            icon: <KeyRound size={14} />,
            onSelect: () => navigate('/change-password'),
          },
          { id: 'logout', label: 'Выйти', icon: <LogOut size={14} />, danger: true, onSelect: () => void signOut() },
        ]}
      />
    </header>
  );
}
